import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faSpinner,
  faFilter,
  faSync,
  faChevronDown,
  faChevronRight,
  faArrowDown,
  faArrowUp,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { accountApi } from '../api/accountApi';

/** @see https://docs.alpaca.markets/us/docs/account-activities */
const ACTIVITY_META = {
  FILL: { label: 'Fill', group: 'trade', tone: 'action' },
  TRANS: { label: 'Cash transfer', group: 'cash', tone: 'muted' },
  MISC: { label: 'Misc', group: 'other', tone: 'muted' },
  ACATC: { label: 'ACATS cash', group: 'transfer', tone: 'muted' },
  ACATS: { label: 'ACATS securities', group: 'transfer', tone: 'muted' },
  CFEE: { label: 'Crypto fee', group: 'fee', tone: 'bear' },
  CGD: { label: 'Cap gain dist.', group: 'div', tone: 'bull' },
  CSD: { label: 'Cash deposit', group: 'cash', tone: 'bull' },
  CSW: { label: 'Cash withdrawal', group: 'cash', tone: 'bear' },
  DIV: { label: 'Dividend', group: 'div', tone: 'bull' },
  DIVCGL: { label: 'Div · LT gain', group: 'div', tone: 'bull' },
  DIVCGS: { label: 'Div · ST gain', group: 'div', tone: 'bull' },
  DIVFEE: { label: 'Dividend fee', group: 'fee', tone: 'bear' },
  DIVFT: { label: 'Div · foreign tax', group: 'div', tone: 'warning' },
  DIVNRA: { label: 'Div · NRA withhold', group: 'div', tone: 'warning' },
  DIVROC: { label: 'Div · return of capital', group: 'div', tone: 'muted' },
  DIVTW: { label: 'Div · TEFRA', group: 'div', tone: 'warning' },
  DIVTXEX: { label: 'Div · tax exempt', group: 'div', tone: 'bull' },
  FEE: { label: 'Fee', group: 'fee', tone: 'bear' },
  FOPT: { label: 'Free of payment', group: 'transfer', tone: 'muted' },
  INT: { label: 'Interest', group: 'cash', tone: 'bull' },
  INTNRA: { label: 'Interest · NRA', group: 'cash', tone: 'warning' },
  INTTW: { label: 'Interest · TEFRA', group: 'cash', tone: 'warning' },
  JNL: { label: 'Journal', group: 'journal', tone: 'muted' },
  JNLC: { label: 'Journal cash', group: 'journal', tone: 'muted' },
  JNLS: { label: 'Journal stock', group: 'journal', tone: 'muted' },
  MA: { label: 'Merger / acquisition', group: 'corp', tone: 'warning' },
  NC: { label: 'Name change', group: 'corp', tone: 'muted' },
  OPASN: { label: 'Option assignment', group: 'options', tone: 'warning' },
  OPEXP: { label: 'Option expiration', group: 'options', tone: 'muted' },
  OPXRC: { label: 'Option exercise', group: 'options', tone: 'action' },
  PTC: { label: 'Pass-thru charge', group: 'fee', tone: 'bear' },
  PTR: { label: 'Pass-thru rebate', group: 'cash', tone: 'bull' },
  REORG: { label: 'Reorg', group: 'corp', tone: 'warning' },
  SC: { label: 'Symbol change', group: 'corp', tone: 'muted' },
  SSO: { label: 'Stock spinoff', group: 'corp', tone: 'action' },
  SSP: { label: 'Stock split', group: 'corp', tone: 'action' },
};

const QUICK_FILTERS = [
  { value: '', label: 'All' },
  { value: 'FILL', label: 'Fills' },
  { value: 'DIV', label: 'Dividends' },
  { value: 'TRANS', label: 'Transfers' },
  { value: 'INT', label: 'Interest' },
  { value: 'FEE', label: 'Fees' },
  { value: 'MISC', label: 'Misc' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All activity types' },
  ...Object.entries(ACTIVITY_META).map(([value, meta]) => ({
    value,
    label: `${value} — ${meta.label}`,
  })),
];

const PAGE_SIZE = 50;

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatMoney(value, { signed = false } = {}) {
  const n = num(value);
  if (n == null) return '—';
  const abs = Math.abs(n);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  if (!signed) return n < 0 ? `−${formatted}` : formatted;
  if (n > 0) return `+${formatted}`;
  if (n < 0) return `−${formatted}`;
  return formatted;
}

function formatQty(value) {
  const n = num(value);
  if (n == null) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 6 });
}

function formatWhen(activity) {
  const raw = activity.transaction_time || activity.date;
  if (!raw) return { absolute: '—', relative: '' };
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return { absolute: String(raw), relative: '' };
  }
  const absolute = d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: activity.transaction_time ? '2-digit' : undefined,
  });
  const ms = Date.now() - d.getTime();
  let relative = '';
  if (Number.isFinite(ms) && ms >= 0) {
    const mins = Math.floor(ms / 60000);
    if (mins < 1) relative = 'just now';
    else if (mins < 60) relative = `${mins}m ago`;
    else if (mins < 1440) relative = `${Math.floor(mins / 60)}h ago`;
    else relative = `${Math.floor(mins / 1440)}d ago`;
  }
  return { absolute, relative };
}

function typeMeta(code) {
  return ACTIVITY_META[code] || { label: code || 'Unknown', group: 'other', tone: 'muted' };
}

function toneClasses(tone) {
  switch (tone) {
    case 'bull':
      return 'bg-[var(--lt-bull-dim)] text-[var(--lt-bull)] border-[rgba(61,220,151,0.35)]';
    case 'bear':
      return 'bg-[var(--lt-bear-dim)] text-[var(--lt-bear)] border-[rgba(240,113,103,0.35)]';
    case 'action':
      return 'bg-[var(--lt-action-dim)] text-[var(--lt-action)] border-[rgba(108,140,255,0.35)]';
    case 'warning':
      return 'bg-[rgba(232,184,74,0.14)] text-[var(--lt-warning)] border-[rgba(232,184,74,0.35)]';
    default:
      return 'bg-[var(--lt-hover)] text-[var(--lt-muted)] border-[var(--lt-border)]';
  }
}

function isFill(activity) {
  return String(activity.activity_type || '').toUpperCase() === 'FILL';
}

function fillNotional(activity) {
  const qty = num(activity.qty);
  const price = num(activity.price);
  if (qty == null || price == null) return null;
  const side = String(activity.side || '').toLowerCase();
  // Buy spends cash (−); sell receives (+)
  if (side === 'buy') return -(qty * price);
  if (side === 'sell') return qty * price;
  return qty * price;
}

function activityCash(activity) {
  if (isFill(activity)) return fillNotional(activity);
  return num(activity.net_amount);
}

function detailLines(activity) {
  const lines = [];
  if (isFill(activity)) {
    if (activity.type) lines.push(['Fill type', activity.type]);
    if (activity.order_id) lines.push(['Order', activity.order_id]);
    if (activity.cum_qty != null) lines.push(['Cumulative qty', formatQty(activity.cum_qty)]);
    if (activity.leaves_qty != null) lines.push(['Leaves qty', formatQty(activity.leaves_qty)]);
  } else {
    if (activity.cusip) lines.push(['CUSIP', activity.cusip]);
    if (activity.per_share_amount != null) {
      lines.push(['Per share', formatMoney(activity.per_share_amount)]);
    }
    if (activity.description) lines.push(['Description', activity.description]);
  }
  if (activity.id) lines.push(['Activity ID', activity.id]);
  return lines;
}

const AccountActivitiesPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [after, setAfter] = useState('');
  const [until, setUntil] = useState('');
  const [direction, setDirection] = useState('desc');
  const [symbolQuery, setSymbolQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const buildOptions = useCallback(
    (pageToken = null) => {
      const options = {
        direction,
        page_size: PAGE_SIZE,
      };
      // Prefer after/until range; single-day `date` only when both equal
      if (after && until && after === until) {
        options.date = after;
      } else {
        if (after) options.after = after;
        if (until) options.until = until;
      }
      if (pageToken) options.page_token = pageToken;
      return options;
    },
    [after, until, direction]
  );

  const loadActivities = useCallback(
    async ({ append = false, pageToken = null } = {}) => {
      try {
        if (append) setLoadingMore(true);
        else {
          setLoading(true);
          setError(null);
        }

        const activityType =
          selectedType && selectedType.trim() !== '' ? selectedType.trim().toUpperCase() : null;
        const response = await accountApi.getActivities(activityType, buildOptions(pageToken));
        const rows = Array.isArray(response.data) ? response.data : [];

        setActivities((prev) => (append ? [...prev, ...rows] : rows));
        const last = rows[rows.length - 1];
        const token = last?.id || null;
        const more = rows.length >= PAGE_SIZE && Boolean(token);
        setNextPageToken(more ? token : null);
        setHasMore(more);
      } catch (err) {
        console.error('Error loading activities:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load activities');
        if (!append) setActivities([]);
        setHasMore(false);
        setNextPageToken(null);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [selectedType, buildOptions]
  );

  useEffect(() => {
    loadActivities({ append: false });
  }, [loadActivities]);

  const filtered = useMemo(() => {
    const q = symbolQuery.trim().toUpperCase();
    if (!q) return activities;
    return activities.filter((a) => String(a.symbol || '').toUpperCase().includes(q));
  }, [activities, symbolQuery]);

  const summary = useMemo(() => {
    let fills = 0;
    let buys = 0;
    let sells = 0;
    let dividends = 0;
    let fees = 0;
    let cashImpact = 0;
    for (const a of filtered) {
      const type = String(a.activity_type || '').toUpperCase();
      const cash = activityCash(a);
      if (cash != null) cashImpact += cash;
      if (type === 'FILL') {
        fills += 1;
        if (String(a.side || '').toLowerCase() === 'buy') buys += 1;
        else if (String(a.side || '').toLowerCase() === 'sell') sells += 1;
      } else if (type.startsWith('DIV') || type === 'CGD') {
        dividends += 1;
      } else if (type === 'FEE' || type === 'CFEE' || type === 'DIVFEE' || type === 'PTC') {
        fees += 1;
      }
    }
    return { fills, buys, sells, dividends, fees, cashImpact, total: filtered.length };
  }, [filtered]);

  const clearFilters = () => {
    setSelectedType('');
    setAfter('');
    setUntil('');
    setDirection('desc');
    setSymbolQuery('');
  };

  return (
    <main className="flex-1 container mx-auto px-4 lg:px-6 py-3">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--lt-text)] flex items-center tracking-tight">
            <FontAwesomeIcon icon={faHistory} className="mr-2 text-[var(--lt-action)]" />
            Account Activities
          </h1>
          <p className="text-xs text-[var(--lt-muted)] mt-1">
            Trade fills and non-trade ledger events from Alpaca ·{' '}
            <a
              href="https://docs.alpaca.markets/us/docs/account-activities"
              target="_blank"
              rel="noreferrer"
              className="text-[var(--lt-action)] hover:underline inline-flex items-center gap-1"
            >
              API reference
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[9px]" />
            </a>
          </p>
        </div>
        <button
          type="button"
          onClick={() => loadActivities({ append: false })}
          disabled={loading}
          className="px-2.5 py-1.5 text-xs rounded border border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] disabled:opacity-50"
        >
          <FontAwesomeIcon icon={loading ? faSpinner : faSync} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary strip */}
      <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          { label: 'Shown', value: String(summary.total), sub: 'this page set' },
          { label: 'Fills', value: String(summary.fills), sub: `${summary.buys} buy · ${summary.sells} sell` },
          { label: 'Dividends', value: String(summary.dividends), sub: 'DIV / CGD' },
          { label: 'Fees', value: String(summary.fees), sub: 'FEE / PTC' },
          {
            label: 'Est. cash Δ',
            value: formatMoney(summary.cashImpact, { signed: true }),
            sub: 'fills + NTA',
            tone: summary.cashImpact > 0 ? 'bull' : summary.cashImpact < 0 ? 'bear' : null,
          },
          {
            label: 'Filter',
            value: selectedType || 'ALL',
            sub: direction === 'desc' ? 'newest first' : 'oldest first',
          },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-lg border border-[var(--lt-border)] bg-[var(--lt-surface)] px-3 py-2"
          >
            <div className="text-[10px] uppercase tracking-wide text-[var(--lt-faint)]">{card.label}</div>
            <div
              className={`mt-0.5 font-mono text-sm tabular-nums ${
                card.tone === 'bull'
                  ? 'text-[var(--lt-bull)]'
                  : card.tone === 'bear'
                    ? 'text-[var(--lt-bear)]'
                    : 'text-[var(--lt-text)]'
              }`}
            >
              {card.value}
            </div>
            <div className="text-[10px] text-[var(--lt-faint)] mt-0.5">{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-3 rounded-xl border border-[var(--lt-border)] bg-[var(--lt-surface)] overflow-hidden">
        <div className="px-3 py-2 border-b border-[var(--lt-border)] flex items-center gap-2">
          <FontAwesomeIcon icon={faFilter} className="text-[var(--lt-faint)] text-xs" />
          <span className="text-xs font-semibold text-[var(--lt-text)]">Filters</span>
          <span className="text-[10px] text-[var(--lt-faint)] ml-auto">
            page_size {PAGE_SIZE} · page_token pagination
          </span>
        </div>

        <div className="px-3 py-2 flex flex-wrap gap-1.5 border-b border-[var(--lt-border)]">
          {QUICK_FILTERS.map((f) => {
            const active = selectedType === f.value;
            return (
              <button
                key={f.value || 'all'}
                type="button"
                onClick={() => setSelectedType(f.value)}
                className={`px-2.5 py-1 text-[11px] rounded border transition-colors ${
                  active
                    ? 'bg-[var(--lt-action-dim)] text-[var(--lt-action)] border-[rgba(108,140,255,0.4)]'
                    : 'border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
              Activity type
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)] focus:outline-none focus:border-[var(--lt-action)]"
            >
              {TYPE_OPTIONS.map((type) => (
                <option key={type.value || 'all'} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
              After
            </label>
            <input
              type="date"
              value={after}
              onChange={(e) => setAfter(e.target.value)}
              className="w-full px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)] focus:outline-none focus:border-[var(--lt-action)]"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
              Until
            </label>
            <input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="w-full px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)] focus:outline-none focus:border-[var(--lt-action)]"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
              Symbol contains
            </label>
            <input
              value={symbolQuery}
              onChange={(e) => setSymbolQuery(e.target.value.toUpperCase())}
              placeholder="e.g. AAPL"
              spellCheck={false}
              className="w-full px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)] font-mono focus:outline-none focus:border-[var(--lt-action)]"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
              Sort
            </label>
            <div className="flex gap-2">
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                className="flex-1 px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)] focus:outline-none focus:border-[var(--lt-action)]"
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>
              <button
                type="button"
                onClick={clearFilters}
                className="px-2 py-1.5 text-[11px] rounded-md border border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]"
                title="Clear filters"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[var(--lt-border)] bg-[var(--lt-surface)] overflow-hidden">
        {loading && activities.length === 0 ? (
          <div className="p-10 text-center text-[var(--lt-muted)]">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl mb-2" />
            <p className="text-sm">Loading ledger…</p>
          </div>
        ) : error ? (
          <div className="p-10 text-center">
            <p className="text-sm text-[var(--lt-bear)]">{error}</p>
            <button
              type="button"
              onClick={() => loadActivities({ append: false })}
              className="mt-3 px-3 py-1.5 text-xs rounded-md bg-[var(--lt-action-dim)] text-[var(--lt-action)] border border-[rgba(108,140,255,0.35)]"
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[var(--lt-muted)] text-sm">
            No activities match these filters.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--lt-border)] text-[10px] uppercase tracking-wide text-[var(--lt-faint)]">
                    <th className="px-3 py-2 text-left font-medium w-6" />
                    <th className="px-3 py-2 text-left font-medium">When</th>
                    <th className="px-3 py-2 text-left font-medium">Type</th>
                    <th className="px-3 py-2 text-left font-medium">Symbol</th>
                    <th className="px-3 py-2 text-left font-medium">Side</th>
                    <th className="px-3 py-2 text-right font-medium">Qty</th>
                    <th className="px-3 py-2 text-right font-medium">Price</th>
                    <th className="px-3 py-2 text-right font-medium">Cash</th>
                    <th className="px-3 py-2 text-left font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((activity) => {
                    const id = activity.id || `${activity.order_id}-${activity.transaction_time}`;
                    const open = expandedId === id;
                    const code = String(activity.activity_type || '').toUpperCase();
                    const meta = typeMeta(code);
                    const when = formatWhen(activity);
                    const cash = activityCash(activity);
                    const side = String(activity.side || '').toLowerCase();
                    const lines = detailLines(activity);

                    return (
                      <React.Fragment key={id}>
                        <tr
                          className="border-b border-[var(--lt-border)] hover:bg-[var(--lt-hover)]/60 cursor-pointer"
                          onClick={() => setExpandedId(open ? null : id)}
                        >
                          <td className="px-3 py-2 text-[var(--lt-faint)]">
                            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="text-[9px]" />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="font-mono text-[var(--lt-text)] tabular-nums">{when.absolute}</div>
                            {when.relative && (
                              <div className="text-[10px] text-[var(--lt-faint)]">{when.relative}</div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold ${toneClasses(meta.tone)}`}
                              title={code}
                            >
                              {code || '—'}
                            </span>
                            <div className="text-[10px] text-[var(--lt-faint)] mt-0.5">{meta.label}</div>
                          </td>
                          <td className="px-3 py-2 font-mono font-semibold tracking-wide text-[var(--lt-text)]">
                            {activity.symbol || '—'}
                          </td>
                          <td className="px-3 py-2">
                            {side === 'buy' || side === 'sell' ? (
                              <span
                                className={`inline-flex items-center gap-1 font-semibold ${
                                  side === 'buy' ? 'text-[var(--lt-bull)]' : 'text-[var(--lt-bear)]'
                                }`}
                              >
                                <FontAwesomeIcon
                                  icon={side === 'buy' ? faArrowUp : faArrowDown}
                                  className="text-[9px]"
                                />
                                {side.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-[var(--lt-faint)]">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--lt-text)]">
                            {activity.qty != null ? formatQty(activity.qty) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--lt-text)]">
                            {activity.price != null
                              ? formatMoney(activity.price)
                              : activity.per_share_amount != null
                                ? formatMoney(activity.per_share_amount)
                                : '—'}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${
                              cash != null && cash > 0
                                ? 'text-[var(--lt-bull)]'
                                : cash != null && cash < 0
                                  ? 'text-[var(--lt-bear)]'
                                  : 'text-[var(--lt-muted)]'
                            }`}
                          >
                            {cash != null ? formatMoney(cash, { signed: true }) : '—'}
                          </td>
                          <td className="px-3 py-2 text-[var(--lt-muted)] max-w-[14rem] truncate">
                            {isFill(activity)
                              ? activity.type === 'partial_fill'
                                ? `Partial · leaves ${formatQty(activity.leaves_qty)}`
                                : 'Full fill'
                              : activity.per_share_amount != null
                                ? `${formatQty(activity.qty)} × ${formatMoney(activity.per_share_amount)}`
                                : meta.label}
                          </td>
                        </tr>
                        {open && (
                          <tr className="border-b border-[var(--lt-border)] bg-[var(--lt-input)]/40">
                            <td colSpan={9} className="px-4 py-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
                                {lines.map(([k, v]) => (
                                  <div key={k} className="flex gap-2 text-[11px] min-w-0">
                                    <span className="text-[var(--lt-faint)] shrink-0 w-28">{k}</span>
                                    <span className="font-mono text-[var(--lt-text)] truncate" title={String(v)}>
                                      {v}
                                    </span>
                                  </div>
                                ))}
                                {!lines.length && (
                                  <span className="text-[11px] text-[var(--lt-faint)]">No extra fields</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-3 py-2.5 border-t border-[var(--lt-border)] flex flex-wrap items-center justify-between gap-2">
              <div className="text-[11px] text-[var(--lt-faint)]">
                Showing {filtered.length}
                {symbolQuery ? ` of ${activities.length} loaded` : ''} activities
                {hasMore ? ' · more available' : ''}
              </div>
              {hasMore && (
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={() => loadActivities({ append: true, pageToken: nextPageToken })}
                  className="px-3 py-1.5 text-xs rounded-md border border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] disabled:opacity-50"
                >
                  <FontAwesomeIcon
                    icon={loadingMore ? faSpinner : faHistory}
                    className={`mr-1.5 ${loadingMore ? 'animate-spin' : ''}`}
                  />
                  {loadingMore ? 'Loading…' : 'Load older'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default AccountActivitiesPage;
